"use client";

import { useEffect, useState } from "react";

import { getToken } from "@/lib/api/auth";

/**
 * Le rendu serveur est anonyme, et il doit le rester.
 *
 * Le jeton vit dans `localStorage` : le serveur ne peut pas savoir qui demande
 * la page, et c'est ce qui permet d'en servir une version indexable. Mais cela
 * veut dire que le HTML initial montre toujours la vue du visiteur non
 * connecté, donc des parcours verrouillés à quelqu'un qui a le droit de les
 * ouvrir.
 *
 * Ce hook rattrape ce décalage : si un jeton existe, il redemande la même
 * ressource au montage, cette fois signée. Sinon il ne fait rien, et la vue du
 * serveur est déjà la bonne.
 *
 * On ne relit pas systématiquement : pour un visiteur anonyme, l'appel
 * rendrait exactement ce que le serveur a déjà écrit.
 */
export interface AuthedView<T> {
  readonly view: T;
  /**
   * La vue tenue est celle qui fera foi.
   *
   * Vrai tout de suite pour un visiteur anonyme — le serveur avait déjà
   * raison —, sinon quand la relecture signée a abouti *ou* échoué : dans les
   * deux cas il n'y a plus rien à attendre.
   *
   * Ce drapeau existe pour la mesure, pas pour l'affichage. L'écran, lui, a
   * raison de montrer la vue anonyme en attendant : elle est correcte à une
   * fraction de seconde près, et un squelette coûterait plus qu'il ne
   * rapporte. Mais compter un parcours comme verrouillé pendant cette
   * fraction-là attribuerait à tous les gens connectés un mur qu'ils ne voient
   * jamais — et c'est précisément le chiffre que `program_opened` sert à lire.
   */
  readonly settled: boolean;
}

export function useAuthedView<T>(
  fallback: T,
  fetcher: () => Promise<T>,
): AuthedView<T> {
  const [view, setView] = useState<T>(fallback);
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    setView(fallback);
  }, [fallback]);

  useEffect(() => {
    if (getToken() === null) {
      setSettled(true);

      return;
    }

    let cancelled = false;

    setSettled(false);

    fetcher()
      .then((fresh) => {
        if (!cancelled) {
          setView(fresh);
        }
      })
      .catch(() => {
        // Un échec laisse la vue du serveur, qui est correcte pour un anonyme.
        // Mieux vaut un parcours affiché fermé qu'un écran en erreur.
      })
      .finally(() => {
        if (!cancelled) {
          setSettled(true);
        }
      });

    return () => {
      cancelled = true;
    };
    // `fetcher` est recréé à chaque rendu chez l'appelant : on se cale sur la
    // valeur servie par le serveur, qui est ce qui change réellement.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fallback]);

  return { view, settled };
}
